import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined";
import EastOutlinedIcon from "@mui/icons-material/EastOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { fetchAPI, fetchUserAppAPI } from "../../utils/api";
import AddAddressModal from "../../components/AddAddressModal";
import PersonIcon from "@mui/icons-material/Person";
import GroupsIcon from "@mui/icons-material/Groups";

export default function NurgingBooking() {
  const navigate = useNavigate();

  const [selectedServices, setSelectedServices] = useState([]);
  const [patientName, setPatientName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [gender, setGender] = useState("");
  const [age, setAge] = useState("");
  const [loading, setLoading] = useState(false);

  // Address states
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showAddressModal, setShowAddressModal] = useState(false);

  // Notification Toast
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "success" });
    }, 3500);
  };

  const fetchUserAddresses = async (userId) => {
    try {
      const response = await fetchUserAppAPI("list-address", {
        method: "POST",
        body: JSON.stringify({ user_id: userId }),
      });
      if (
        response &&
        response.status === 200 &&
        Array.isArray(response.result)
      ) {
        setAddresses(response.result);

        const savedSel = localStorage.getItem("selectedAddress");
        let activeAddr = null;
        if (savedSel) {
          try {
            const parsedSel = JSON.parse(savedSel);
            activeAddr = response.result.find(
              (a) => a.address_id === parsedSel.address_id,
            );
          } catch (e) {}
        }
        if (!activeAddr && response.result.length > 0) {
          activeAddr =
            response.result.find((a) => a.address_default == 1) ||
            response.result[0];
        }

        if (activeAddr) {
          setSelectedAddressId(activeAddr.address_id);
        }
      }
    } catch (err) {
      console.error("Error fetching user addresses:", err);
    }
  };

  useEffect(() => {
    // 1. Fetch saved user info
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const parsed = JSON.parse(userStr);
        const fullName =
          `${parsed.user_first_name || ""} ${parsed.user_last_name || ""}`.trim();
        setPatientName(fullName);
        setPhone(parsed.user_mobile || "");
        setEmail(parsed.user_email || "");
        if (parsed.user_id) {
          fetchUserAddresses(parsed.user_id);
        }
      } catch (e) {
        console.error("Failed to parse user", e);
      }
    }

    // 2. Fetch selected services / cart items
    const savedCart =
      localStorage.getItem("selectedShortTerm") ||
      localStorage.getItem("selectedServicesCart") ||
      localStorage.getItem("selectedLongTerm");

    if (savedCart) {
      try {
        const parsed = JSON.parse(savedCart);
        const arr = Array.isArray(parsed) ? parsed : [parsed];
        setSelectedServices(arr);
      } catch (e) {
        console.error("Failed to parse selected services", e);
      }
    }

    const onAddressChange = () => {
      const savedSel = localStorage.getItem("selectedAddress");
      if (savedSel) {
        try {
          const parsedSel = JSON.parse(savedSel);
          setSelectedAddressId(parsedSel.address_id);
        } catch (e) {}
      }
    };

    window.addEventListener("addressChanged", onAddressChange);
    return () => {
      window.removeEventListener("addressChanged", onAddressChange);
    };
  }, []);

  const handleAddAddressSuccess = (newAddress) => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const parsed = JSON.parse(userStr);
        fetchUserAddresses(parsed.user_id);
      } catch (e) {}
    }
    if (newAddress) {
      setSelectedAddressId(newAddress.address_id);
    }
  };

  const handleSelectLocalAddress = (addr) => {
    setSelectedAddressId(addr.address_id);
    localStorage.setItem("selectedAddress", JSON.stringify(addr));
    window.dispatchEvent(new Event("addressChanged"));
  };

  const packageFees = selectedServices.reduce((sum, s) => {
    const priceStr =
      s.price !== undefined && s.price !== null ? String(s.price) : "0";
    const numericPrice =
      parseFloat(priceStr.replace(/,/g, "").replace(/[^\d.]/g, "")) || 0;
    return sum + numericPrice;
  }, 0);

  const gst = packageFees * 0.18;
  const totalPayable = packageFees + gst;

  const handleBookNow = async (e) => {
    e.preventDefault();

    const userStr = localStorage.getItem("user");
    if (!userStr) {
      showToast("Please login first to complete your booking.", "warning");
      window.dispatchEvent(new Event("openLoginModal"));
      return;
    }

    let user;
    try {
      user = JSON.parse(userStr);
    } catch (err) {
      showToast("Invalid session. Please login again.", "warning");
      window.dispatchEvent(new Event("openLoginModal"));
      return;
    }

    if (!patientName || !phone) {
      showToast("Please fill in patient name and phone number.", "warning");
      return;
    }

    if (selectedServices.length === 0) {
      showToast("No service selected for booking.", "warning");
      return;
    }

    try {
      setLoading(true);

      const bookingPayload = {
        user_id: user.user_id,
        ord_book_for: "self",
        patient_name: patientName,
        patient_phone: phone,
        patient_email: email,
        gender: gender,
        age: age,
        address_id: selectedAddressId,
        items: selectedServices.map((s) => ({
          service_id: s.service_id || s.id,
          service_title:
            s.service_title ||
            s.title ||
            s.name ||
            s.sp_title ||
            "Nursing Service",
          service_type_id: s.service_type_id || s.st_id,
          service_type_title: s.service_type_title || s.st_title,
          package_id: s.package_id || s.sp_id,
          package_title:
            s.package_title ||
            s.sp_title ||
            s.title ||
            s.name ||
            "Service Package",
          package_code: s.package_code || s.sp_code,
          qty: s.quantity || s.qty || 1,
          price:
            parseFloat(
              String(s.price || s.sp_price || 0)
                .replace(/,/g, "")
                .replace(/[^\d.]/g, ""),
            ) || 0,
          sale_price:
            parseFloat(
              String(s.sale_price || s.sp_sale_price || s.price || 0)
                .replace(/,/g, "")
                .replace(/[^\d.]/g, ""),
            ) || 0,
        })),
        services: selectedServices,
        total_amount: totalPayable,
      };

      let response = null;
      try {
        response = await fetchAPI("CaregiverController/create_order", {
          method: "POST",
          body: JSON.stringify(bookingPayload),
        });
      } catch (err) {
        response = await fetchUserAppAPI("services-create-order", {
          method: "POST",
          body: JSON.stringify(bookingPayload),
        });
      }

      // Clear local cart storage after successful booking
      localStorage.removeItem("selectedShortTerm");
      localStorage.removeItem("selectedServicesCart");
      localStorage.removeItem("selectedLongTerm");
      localStorage.removeItem("activeCartType");
      window.dispatchEvent(new Event("cartUpdated"));

      showToast("Booking submitted successfully!", "success");
      setTimeout(() => {
        navigate("/thankyou", {
          state: {
            orderNo:
              response?.result?.order_no ||
              response?.result?.booking_id ||
              "CARE-" + Date.now().toString().slice(-6),
          },
        });
      }, 1500);
    } catch (err) {
      console.error("Booking error:", err);
      // Fallback redirection to thankyou page
      navigate("/thankyou", {
        state: { orderNo: "CARE-" + Date.now().toString().slice(-6) },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />

      {toast.show && (
        <div className={`custom-toast-notification custom-toast-${toast.type}`}>
          <CheckCircleIcon />
          <span>{toast.message}</span>
        </div>
      )}

      <div className="test_bredcum_sec">
        <div className="container-fluid">
          <div className="test_bredcum">
            <Link to="/">Home</Link>
            <span>&gt;</span>
            <span className="name_p">Booking Form</span>
          </div>
        </div>
      </div>

      <div className="booking_form_main">
        <div className="container-fluid">
          <div className="inner_booking_form">
            <form onSubmit={handleBookNow}>
              <div className="row">
                {/* LEFT SECTION */}
                <div className="col-lg-7">
                  <div className="booking_box booking_first_sec">
                    <h2>Who are you booking For?</h2>
                    <div className="radio_btn_booking_form">
                      <input
                        type="radio"
                        id="self"
                        name="booking"
                        defaultChecked
                        hidden
                      />
                      <label htmlFor="self" className="booking_card">
                        <div className="radio_circle"></div>
                        <div className="icon-bookform">
                          <PersonIcon />
                        </div>
                        <h3>Booking For Self</h3>
                      </label>

                      <input type="radio" id="other" name="booking" hidden />
                      <label htmlFor="other" className="booking_card">
                        <div className="radio_circle"></div>
                        <div className="icon-bookform">
                          <GroupsIcon />
                        </div>
                        <h3>Booking for Others</h3>
                      </label>
                    </div>
                  </div>
                  {/* Patient Information Section */}
                  <div className="booking_box patient_info_form">
                    <h3>Patient Information</h3>
                    <div className="form_patient_info">
                      <div className="row">
                        <div className="col-12 mb-3">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Patient Name *"
                            required
                            value={patientName}
                            onChange={(e) => setPatientName(e.target.value)}
                          />
                        </div>

                        <div className="col-md-6 mb-3">
                          <input
                            type="tel"
                            className="form-control"
                            placeholder="Phone Number *"
                            required
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                          />
                        </div>

                        <div className="col-md-6 mb-3">
                          <input
                            type="email"
                            className="form-control"
                            placeholder="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                          />
                        </div>

                        <div className="col-md-6 col-6 mb-3">
                          <select
                            className="form-select"
                            value={gender}
                            onChange={(e) => setGender(e.target.value)}
                          >
                            <option value="">Select Gender</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>

                        <div className="col-md-6 col-6 mb-3">
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Age"
                            min="0"
                            value={age}
                            onChange={(e) => setAge(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sample Collection / Service Address Section */}
                  <div className="booking_box sample_address">
                    <h3>Service Address</h3>
                    <div className="samele_add_radio_btn">
                      {addresses.length === 0 ? (
                        <p className="text-muted small p-2">
                          No saved address found. Please add a new address.
                        </p>
                      ) : (
                        addresses.map((addr) => (
                          <label className="address_box" key={addr.address_id}>
                            <input
                              type="radio"
                              name="address"
                              checked={selectedAddressId === addr.address_id}
                              onChange={() => handleSelectLocalAddress(addr)}
                            />
                            <span className="radio_circle"></span>
                            <div className="address_content">
                              <h4 style={{ textTransform: "capitalize" }}>
                                {addr.address_title || "Care"} Address
                              </h4>
                              <p>
                                {addr.address_name ? (
                                  <strong>
                                    {addr.address_name} ({addr.address_phone})
                                    <br />
                                  </strong>
                                ) : null}
                                {addr.address_plot_no
                                  ? addr.address_plot_no + ", "
                                  : ""}
                                {addr.address_locility}, {addr.address_area},{" "}
                                {addr.address_city_name ||
                                  (addr.address_city === "1" ||
                                  addr.address_city === 1
                                    ? "Jaipur"
                                    : addr.address_city)}
                                ,{" "}
                                {addr.address_state_name ||
                                  (addr.address_state === "1" ||
                                  addr.address_state === 1
                                    ? "Rajasthan"
                                    : addr.address_state)}{" "}
                                - {addr.address_pincode}
                              </p>
                            </div>
                          </label>
                        ))
                      )}

                      <div className="add_more_btn">
                        <button
                          type="button"
                          onClick={() => setShowAddressModal(true)}
                        >
                          <div>
                            <AddOutlinedIcon /> Add New Address
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* RIGHT SECTION: SUMMARY */}
                <div className="col-lg-5">
                  <div className="for_sticky_section">
                    <div className="summary_sec booking_box">
                      <h3 className="summary-heading">Summary</h3>
                      <div className="price_booking">
                        {selectedServices.map((service, idx) => (
                          <div
                            className="name_price_booking"
                            key={service.id || idx}
                          >
                            <h4 className="">
                              {service.name ||
                                service.title ||
                                "Healthcare Service"}
                            </h4>
                            <h5>{service.price}</h5>
                          </div>
                        ))}
                        {selectedServices.length > 1 && (
                          <div
                            className="name_price_booking"
                            style={{
                              borderTop: "1px solid #eee",
                              paddingTop: "8px",
                            }}
                          >
                            <h4> Subtotal</h4>
                            <h5>₹{packageFees.toFixed(2)}</h5>
                          </div>
                        )}
                        <div className="name_price_booking">
                          <h4>GST (18%)</h4>
                          <h5>₹{gst.toFixed(2)}</h5>
                        </div>
                      </div>
                      <div className="total_price_booking">
                        <h4>Total</h4>
                        <h5>₹ {totalPayable.toFixed(2)}</h5>
                      </div>
                    </div>

                    <div class="form-check term-check">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        value=""
                        id="checkChecked"
                        checked
                      />
                      <label class="form-check-label" for="checkChecked">
                        By Clicking you will be agree to our{" "}
                        <Link to="/terms-and-condition">
                          {" "}
                          Terms & Conditions
                        </Link>{" "}
                        and <Link to="/privacy-policy"> Privacy Policy</Link>
                      </label>
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="checkout-btn"
                    >
                      {loading ? "Booking..." : "Book Now"}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <Footer />

      <AddAddressModal
        show={showAddressModal}
        onClose={() => setShowAddressModal(false)}
        onSuccess={handleAddAddressSuccess}
      />
    </>
  );
}
